import type { ResearchRu } from '../i18n';

/** Russian overlays for the research registry, keyed by slug. Missing fields
 *  fall back to English. Brand/theory names (Agent Native Universe, Balansis,
 *  UWT / Unified Wholeness Theory) stay in Latin. */
export const researchRu: Record<string, ResearchRu> = {
  'agent-native-software': {
    title: 'Агент-native ПО',
    area: 'Мультиагентные системы',
    hypothesis:
      'Когда агенты рассматриваются как пользователи ПО первого класса — с согласованием, собственностью и экономикой, встроенными в среду исполнения, — возникают структуры координации, которым не нужен центральный оркестратор.',
    problem:
      'Сегодняшние агентные фреймворки прикручивают агентов к ПО, скроенному под людей: один оркестратор, очередь задач и хрупкие связки. Такая форма ограничивает автономию воображением оркестратора.',
    stateLabel: 'Открытый артефакт опубликован',
    methods: [
      'Проектирование среды исполнения: согласование отношений между агентами',
      'Экономика ресурсов внутри среды исполнения',
      'Византийское согласие между недоверенными узлами',
      'Рекурсивная организация: группы, сворачивающиеся в составных агентов',
    ],
    findings: [
      'Работающая среда исполнения под лицензией MIT (Agent Native Universe), демонстрирующая согласование, экономику и рекурсивную организацию',
      'Сворачивание и разворачивание групп агентов выразимо как операция среды исполнения первого класса',
    ],
  },
  'multi-agent-consensus': {
    title: 'Консенсус и экономики для агентов',
    area: 'Распределённые системы',
    hypothesis:
      'Самоорганизующимся коллективам агентов нужны те же примитивы, что человеческим рынкам и распределённым базам данных: ограниченные ресурсы, согласуемые договорённости и консенсус, переживающий сбойных или враждебных участников.',
    problem:
      'Без ограниченности ресурсов и механизма согласия рои агентов либо заходят в тупик, либо усиливают ошибки. Большинство фреймворков игнорируют и то, и другое в надежде, что LLM сам разберётся.',
    stateLabel: 'Открытый артефакт опубликован',
    methods: [
      'Протоколы византийского согласия между узлами-агентами',
      'Ценообразование ресурсов и распространение бюджета по группам агентов',
      'Внедрение отказов: враждебные и неисправные агенты в коллективе',
    ],
    findings: [
      'Византийское согласие практично на масштабе рабочих групп агентов',
      'Экономика ресурсов измеримо формирует поведение агентов без явного программирования',
    ],
  },
  'knowledge-graphs': {
    title: 'Живые модели знаний',
    area: 'Системы знаний',
    hypothesis:
      'Предметная область становится пригодной для преподавания и поиска, когда представлена как связная модель — сущности, связи, происхождение, — которая обновляется вместе с областью, а не как статический индекс.',
    problem:
      'Документы копятся; смысл — нет. Поиск без структуры возвращает фрагменты; структура без происхождения возвращает уверенную бессмыслицу.',
    stateLabel: 'Активно — используется в продакшене',
    methods: [
      'Извлечение сущностей и связей в графовую модель',
      'Семантический поиск по окрестностям графа',
      'Отслеживание происхождения: каждый узел знает, откуда он взялся',
      'Непрерывное обновление по мере изменения исходных материалов',
    ],
    findings: [
      'Знание в форме графа превосходит плоский поиск на областях масштаба учебной программы',
      'Именно происхождение превращает поиск в то, чему может доверять продукт',
    ],
  },
  'adaptive-mastery': {
    title: 'Адаптивные модели усвоения',
    area: 'Человеко-машинное обучение',
    hypothesis:
      'Ученик, который видит, где именно застрял, и получает наименьший следующий шаг, дающий видимый результат, возвращается на свою траекторию обучения — мотивация рождается из успеха, а не выдаётся авансом.',
    problem:
      'Линейные курсы исходят из того, что никто не отстаёт. Отстают все. Интересная задача — это диагностика и восстановление, а не доставка контента.',
    stateLabel: 'Активно — используется в продакшене',
    methods: [
      'Оценка усвоения по графу знаний предмета',
      'Диагностика пробелов: поиск проваленного пререквизита, а не проваленного упражнения',
      'Выбор следующего шага, настроенный на заметные небольшие победы',
    ],
    findings: [
      'Последовательность заданий «сначала пробелы» меняет поведение ученика за дни, а не за месяцы',
      'Усвоение нужно моделировать на графе, а не на списке упражнений',
    ],
  },
  'compensated-numerics': {
    title: 'Вычисления, которые переживают особые точки',
    area: 'Численные основания',
    hypothesis:
      'Численные конвейеры можно строить так, чтобы деление на ноль, переполнение и потеря точности мягко ухудшали результат, а не уничтожали его.',
    problem:
      'Один NaN отравляет тензор; одно переполнение убивает прогон обучения. Стандартный ответ — защитное клиппирование повсюду, которое незаметно меняет саму математику.',
    stateLabel: 'Созревает — библиотека открыто опубликована',
    methods: [
      'Вычислительная библиотека (Balansis), проверяемая как численная основа остальных слоёв',
      'Стресс-наборы вокруг особых точек и экстремальных величин',
    ],
    findings: [
      'Подход выдерживает стресс-наборы лаборатории, а несущая его библиотека открыта — механизм можно прочитать, а не принимать на веру',
    ],
  },
  'grown-networks': {
    title: 'Сети, которые выращены, а не спроектированы',
    area: 'Нейроморфный AI',
    hypothesis:
      'Структура сети, которая развивается — как это делает биологическая ткань, — способна достигать поведения, до которого спроектированные вручную архитектуры добираются только грубой силой.',
    problem:
      'Поиск архитектуры трактует структуру как гиперпараметр. Биология трактует её как продукт развития. Это совершенно разные пространства поиска.',
    stateLabel: 'Созревает — механизм не опубликован',
    methods: [
      'Субстрат импульсных (спайковых) сетей',
      'Правила обучения, локальные для связи',
      'Структурное развитие вместо фиксированной топологии',
    ],
    findings: [
      'Локальное обучение вместе с развивающейся структурой — жизнеспособный режим обучения на исследовательском масштабе; детали не опубликованы',
    ],
  },
  'agent-orchestration': {
    title: 'Агенты, управляющие реальной инфраструктурой',
    area: 'Прикладные мультиагентные системы',
    hypothesis:
      'Мультиагентные команды могут выполнять реальную операционную работу — развёртывание, диагностику, ревью, — если их автономия ограничена уверенностью, а знания разделяются через общий граф.',
    problem:
      'Демо агентных команд даются легко; агенты, которые касаются продакшен-инфраструктуры и переживают контакт с ней, — нет.',
    stateLabel: 'Активно — внутренние системы',
    methods: [
      'Команды агентов, подключённые к живой инфраструктуре, с автономией, ограниченной уверенностью',
      'Общий граф знаний как среда координации',
      'Эскалация к людям как полноправный шаг протокола',
    ],
    findings: [
      'Связывающее ограничение полезной автономии — это уверенность, а не способности агентов',
    ],
  },
  'unified-wholeness': {
    title: 'Unified Wholeness Theory',
    area: 'Теория',
    hypothesis:
      'Теоретическое направление исследований стоит вести внутри инженерной лаборатории только при условии, что каждое утверждение фальсифицируемо, а каждый эксперимент зарегистрирован.',
    problem:
      'Теория соскальзывает в рассказывание историй в тот момент, когда её утверждения перестают быть проверяемыми. Чтобы держать теорию честной, нужна инженерная дисциплина.',
    stateLabel: 'Пакет препринта готовится',
    methods: [
      'Реестр экспериментов с автоматическими тестами по утверждениям',
      'Пакет препринта, подготовленный к открытой публикации',
    ],
    findings: [
      'Дисциплина реестра экспериментов хорошо переносится из тестирования ПО в теоретическую работу',
    ],
  },
};
